import {
  Box,
  Divider as MuiDivider,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  CubeDimensionEnum,
  CubeMeasureEnum,
  FeatureEnum,
} from '../../services/data/impl/cube/cubeSlice';
import { isBlueberryFeature } from '../../services/data/impl/cube/cubeUtils';
import { Block } from '../../services/entities/impl/blfApi/schemas/block';
import { Customer } from '../../services/entities/impl/blfApi/schemas/customer';
import { Ranch } from '../../services/entities/impl/blfApi/schemas/ranch';
import { Scan } from '../../services/entities/impl/blfApi/schemas/scan';
import { useFeatureFlags } from '../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { DiagnosticFeature } from '../commonUI/viewMode/DiagnosticFeature';
import useCurrServicesAndMeasures from '../customerSummary/hooks/useCurrServicesAndMeasures';
import { toggleBlockButtonWasClicked } from '../map/mapSlice';
import { useRanchControllerContext } from '../ranches/context/RanchViewController';
import {
  RanchPageViewEnum,
  selectRanchPageView,
  setRanchPageView,
} from '../ranches/ranchesSlice';
import { ScanStatusIndicator } from '../scans/ScanSection/components/ScanCard/ScanStatusIndicator';
import { BaseDropdown } from './BaseDropdown';
import BlockReadyBadge from './BlockReadyBadge';
import LoadingSpinnerWrapper from './LoadingSpinnerWrapper';
import MapTableViewToggle from './MapTableViewToggle';
import {
  RanchViewFeaturesEnum,
  ResetItemsEnum,
  resetSelectFields,
  selectCurrAIServiceVersionOverride,
  selectCurrBlockID,
  selectCurrCustomerID,
  selectCurrFeature,
  selectCurrRanchID,
  selectCurrRanchViewFeature,
  selectCurrScanID,
  setCurrAIServiceVersionOverride,
  setCurrBlockByID,
  setCurrCustomerByID,
  setCurrFeature,
  setCurrRanchByID,
  setCurrScanByID,
} from './selectSlice';

const barHeight = '4rem';

interface Props {
  customers: Customer[] | undefined;
  ranchesFiltered: Ranch[] | undefined;
  blocksFiltered?: Block[] | undefined;
  scansFiltered: Scan[] | undefined;
  isLoadingCustomers: boolean;
  isLoadingRanches: boolean;
  isLoadingBlocks: boolean;
  isLoadingScans: boolean;
}

const features = Object.values(FeatureEnum);
const activeMeasures = [
  CubeMeasureEnum.detectionFactAvgCountPerMeter,
  CubeMeasureEnum.DetectionFactHasColorHSV,
  CubeMeasureEnum.DetectionFactAvgLength,
  CubeMeasureEnum.DetectionFactAvgArea,
];

/**
 * Filters the measures dropdown based on certain conditions.
 *
 * @param {Object} params - The parameters for the function.
 * @param {CubeMeasureEnum} params.measure - The measure to be filtered.
 * @param {FeatureEnum} params.currFeature - The current feature.
 * @param {RanchPageViewEnum} params.ranchPageView - The current ranch page view.
 * @param {CubeMeasureEnum[]} params.availableMeasures - The available measures.
 * @return {boolean} Returns true if the measure should be included in the dropdown, false otherwise.
 */
const filterMeasuresDropdown = ({
  measure,
  currFeature,
  ranchPageView,
  availableMeasures,
}: {
  measure: CubeMeasureEnum;
  currFeature: FeatureEnum;
  ranchPageView: RanchPageViewEnum;
  availableMeasures: CubeMeasureEnum[];
}) => {
  const conditions = [
    // feature related excludes
    [FeatureEnum.shoot, FeatureEnum.earlyShoot].includes(currFeature) &&
      measure === CubeMeasureEnum.DetectionFactHasColorHSV,
    isBlueberryFeature(currFeature) &&
      measure === CubeMeasureEnum.DetectionFactAvgLength,

    // view related excludes
    ranchPageView === RanchPageViewEnum.ranchSummary &&
      measure === CubeMeasureEnum.DetectionFactHasColorHSV,

    // global excludes
    measure === CubeMeasureEnum.DetectionFactAvgArea,
  ];

  if (conditions.some((condition) => condition)) {
    return false;
  }

  return availableMeasures.includes(measure);
};

export const SelectHeaderDivider = styled(MuiDivider)(({ theme }) => ({
  width: '1px',
  height: '20px',
  display: 'flex',
  margin: '0 1rem',
  backgroundColor: theme.colors.darkTheme.grey.muted,
}));

const SelectHeader: React.FC<Props> = ({
  customers,
  ranchesFiltered,
  blocksFiltered,
  scansFiltered,
  isLoadingCustomers,
  isLoadingRanches,
  isLoadingBlocks,
  isLoadingScans,
}) => {
  // console.log(
  //   'SELECT HEADER RENDERING WHEN HOVERING ON POINT ON MAP??? WHY??? POSSIBLY BC OF DUCK CONTEXT UPDATE WHEN QUERYING A POINTS DATA? ARE OTHER COMPONENTS RE-RENDERING TOO?'
  // );
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const currCustomerID = useAppSelector(selectCurrCustomerID);
  const currRanchID = useAppSelector(selectCurrRanchID);
  const currBlockID = useAppSelector(selectCurrBlockID);
  const currScanID = useAppSelector(selectCurrScanID);
  const currFeature = useAppSelector(selectCurrFeature);
  const currAIServiceVersionOverride = useAppSelector(
    selectCurrAIServiceVersionOverride
  );
  const { Diagnostics } = useFeatureFlags();
  const ranchPageView = useAppSelector(selectRanchPageView);
  const ranchViewFeature = useAppSelector(selectCurrRanchViewFeature);
  const { currMeasure, changeMeasure } = useRanchControllerContext();
  const { t } = useTranslation();

  // Use the available services and measures separately in this case bc the measures are filtered by the selected feature, but the feature can't be filtered by the currently selected feature because then it will only show that feature when selected.
  const {
    servicesAndMeasuresWithFeatureFilter,
    servicesAndMeasuresWithoutFeatureFilter,
    servicesAndMeasuresWithFeatureFilterAllServiceVersions, // Used to see all service versions for QA version selector
  } = useCurrServicesAndMeasures();

  // When the user is on the ranch summary page and looking at the map, the block dropdown should navigate to the block detail page, otherwise it should act as a filter
  const blockDetailOrBlockFilterToggle =
    ranchPageView === RanchPageViewEnum.ranchSummary &&
    ranchViewFeature === RanchViewFeaturesEnum.RanchMap;

  // Automatically select the latest scan with status "complete" when the user navigates to the block detail page
  useEffect(() => {
    if (
      !currScanID &&
      currBlockID &&
      ranchPageView === RanchPageViewEnum.blockDetail
    ) {
      if (scansFiltered && scansFiltered?.length > 0) {
        const completeScans = scansFiltered.filter(
          (scansFiltered) => scansFiltered.pipelineStatus === 'Completed'
        );
        if (completeScans && completeScans.length > 0) {
          const latestCompleteScan = completeScans[0];
          dispatch(setCurrScanByID(latestCompleteScan.id));
        }
      }
    }
  }, [dispatch, scansFiltered, currScanID, ranchPageView, currBlockID]);

  // Get all available unique service versions for QA mode to allow the user to select a specific version
  const AIServiceVersionOptions = useMemo(() => {
    if (
      servicesAndMeasuresWithFeatureFilterAllServiceVersions.availableServicesRaw
    ) {
      return _.uniq(
        servicesAndMeasuresWithFeatureFilterAllServiceVersions.availableServicesRaw.map(
          (service) => service[CubeDimensionEnum.modelVersion]
        )
      );
    }
    return [];
  }, [
    servicesAndMeasuresWithFeatureFilterAllServiceVersions.availableServicesRaw,
  ]);

  // Reset the version selector if Diagnostics is turned off
  useEffect(() => {
    if (!Diagnostics) {
      dispatch(setCurrAIServiceVersionOverride(''));
    }
  }, [Diagnostics, dispatch]);

  const showFeatureAndMeasureDropdowns = useMemo(
    () =>
      !(
        ranchPageView === RanchPageViewEnum.ranchSummary &&
        ranchViewFeature === RanchViewFeaturesEnum.Tables
      ),
    [ranchPageView, ranchViewFeature]
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: barHeight,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: (theme) => theme.colors.canopy.background.default,
        paddingLeft: '32px',
        maxWidth: '100vw', // TODO: fix this sizing problem another way
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 1,
          alignItems: 'center',
          padding: 2,
          flex: 'initial',
          maxWidth: '80vw', // TODO: fix this sizing problem another way
        }}
      >
        {/* if there is only one customer, don't show the customer dropdown */}
        {customers?.length !== 1 && (
          <DropdownContainer>
            <BaseDropdown
              value={currCustomerID}
              items={customers || []}
              label={t('customers', { ns: 'common' })}
              onChange={(newID) => {
                dispatch(
                  resetSelectFields([
                    ResetItemsEnum.PointId,
                    ResetItemsEnum.Scan,
                    ResetItemsEnum.Block,
                    ResetItemsEnum.Ranch,
                    ResetItemsEnum.AllCubeRelatedFields,
                  ])
                );
                dispatch(setCurrCustomerByID(newID));
              }}
              optionValueGetter={(customer: Customer) => customer.id}
              optionLabelGetter={(customer: Customer) => customer.name}
              optionKeyGetter={(customer: Customer) => customer.id}
            />
            <LoadingSpinnerWrapper isLoading={isLoadingCustomers} />
          </DropdownContainer>
        )}
        <DropdownContainer>
          <BaseDropdown
            value={currRanchID}
            items={ranchesFiltered || []}
            label={t('allRanches', { ns: 'common' })}
            onChange={(newID) => {
              dispatch(
                resetSelectFields([
                  ResetItemsEnum.PointId,
                  ResetItemsEnum.Scan,
                  ResetItemsEnum.Block,
                  ResetItemsEnum.AllCubeRelatedFields,
                ])
              );
              dispatch(setCurrRanchByID(newID));
            }}
            optionValueGetter={(ranch: Ranch) => ranch.id}
            optionLabelGetter={(ranch: Ranch) => ranch.name}
            optionKeyGetter={(ranch: Ranch) => ranch.id}
          />
          <LoadingSpinnerWrapper isLoading={isLoadingRanches} />
        </DropdownContainer>
        <DropdownContainer>
          <BaseDropdown
            value={currBlockID}
            items={blocksFiltered || []}
            label={
              blockDetailOrBlockFilterToggle
                ? t('blockDetail', { ns: 'common' })
                : t('allBlocks', { ns: 'common' })
            }
            onChange={(newID) => {
              dispatch(
                resetSelectFields([
                  ResetItemsEnum.PointId,
                  ResetItemsEnum.Scan,
                  ResetItemsEnum.AllCubeRelatedFields,
                ])
              );
              dispatch(setCurrBlockByID(newID));
              dispatch(toggleBlockButtonWasClicked());
              if (blockDetailOrBlockFilterToggle) {
                dispatch(setRanchPageView(RanchPageViewEnum.blockDetail));
              }
            }}
            optionValueGetter={(block: Block) => block.id}
            optionLabelGetter={(block: Block) => (
              <>
                {block.name}
                <DiagnosticFeature>
                  {' ' /* this space is intentional */}
                  <BlockReadyBadge block={block} />
                </DiagnosticFeature>
              </>
            )}
            optionKeyGetter={(block: Block) => block.id}
          />
          <LoadingSpinnerWrapper isLoading={isLoadingBlocks} />
        </DropdownContainer>
        {ranchPageView === RanchPageViewEnum.blockDetail && (
          <DropdownContainer>
            <BaseDropdown
              value={currScanID}
              items={scansFiltered || []}
              label={t('scans', { ns: 'common' })}
              onChange={(newID) => {
                dispatch(setCurrScanByID(newID));
                dispatch(
                  resetSelectFields([
                    ResetItemsEnum.PointId,
                    ResetItemsEnum.AllCubeRelatedFields,
                  ])
                );
              }}
              optionValueGetter={(scan: Scan) => scan.id}
              optionLabelGetter={(scan: Scan) => (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ mr: '0.25rem' }}>
                    {scan.scanDate}
                  </Typography>
                  <ScanStatusIndicator scan={scan} />
                </Box>
              )}
              optionKeyGetter={(scan: Scan) => scan.id}
              disableDefaultEmptyValue={
                ranchPageView === RanchPageViewEnum.blockDetail
              }
            />
            <LoadingSpinnerWrapper isLoading={isLoadingScans} />
          </DropdownContainer>
        )}
        {showFeatureAndMeasureDropdowns && (
          <DropdownContainer>
            <BaseDropdown
              value={currFeature}
              // Filter out aliased options
              items={features.filter(
                (feature) =>
                  ![FeatureEnum.inflorescence].includes(feature) &&
                  servicesAndMeasuresWithoutFeatureFilter.availableServices.includes(
                    feature
                  )
              )}
              label={t('allFeatures', { ns: 'common' })}
              onChange={(newID: string) => {
                if (Object.values(FeatureEnum).includes(newID as FeatureEnum)) {
                  dispatch(setCurrFeature(newID as FeatureEnum));
                } else if (newID === '') {
                  dispatch(setCurrFeature(FeatureEnum.none));
                }
                dispatch(
                  resetSelectFields([ResetItemsEnum.AIServiceVersionOverride])
                );
              }}
              optionValueGetter={(feature: FeatureEnum) => feature}
              optionLabelGetter={(feature: FeatureEnum) =>
                t(feature, { ns: 'common' })
              }
              optionKeyGetter={(feature: FeatureEnum) => feature}
              disableCriteria={(feature: FeatureEnum) =>
                !servicesAndMeasuresWithoutFeatureFilter.availableServices.includes(
                  feature
                ) ||
                !servicesAndMeasuresWithoutFeatureFilter.availableServices.find(
                  (el) => feature.includes(el)
                )
              }
              disableDefaultEmptyValue={
                ranchPageView === RanchPageViewEnum.blockDetail ||
                (ranchPageView === RanchPageViewEnum.ranchSummary &&
                  ranchViewFeature === RanchViewFeaturesEnum.RanchMap)
              }
            />
          </DropdownContainer>
        )}
        <DiagnosticFeature>
          <DropdownContainer sx={{ ml: '2.1rem' }}>
            <BaseDropdown
              value={currAIServiceVersionOverride}
              items={AIServiceVersionOptions}
              label={t('Version')}
              onChange={(newID: string) => {
                dispatch(setCurrAIServiceVersionOverride(newID));
              }}
              optionValueGetter={(version: string) => version}
              optionLabelGetter={(version: string) => version}
              sx={{
                color: currAIServiceVersionOverride
                  ? theme.colors.status.red.main
                  : 'inherit',
              }}
            />
          </DropdownContainer>
        </DiagnosticFeature>
        {showFeatureAndMeasureDropdowns && (
          <DropdownContainer sx={{ ml: '2.1rem' }}>
            <BaseDropdown
              value={currMeasure as unknown as string}
              items={activeMeasures.filter((measure) =>
                filterMeasuresDropdown({
                  measure,
                  currFeature,
                  ranchPageView,
                  availableMeasures:
                    servicesAndMeasuresWithFeatureFilter.availableMeasures,
                })
              )}
              label={t('allMeasures', { ns: 'common' })}
              onChange={(newID: CubeMeasureEnum) => {
                if (
                  Object.values(CubeMeasureEnum).includes(
                    newID as CubeMeasureEnum
                  )
                ) {
                  changeMeasure(newID);
                }
              }}
              optionValueGetter={(measure: CubeMeasureEnum) => measure}
              optionLabelGetter={(measure: CubeMeasureEnum) => {
                if (
                  currFeature === FeatureEnum.berries &&
                  measure === CubeMeasureEnum.DetectionFactAvgLength
                ) {
                  return t('Diameter', { ns: 'common' });
                }

                if (
                  [FeatureEnum.shoot, FeatureEnum.earlyShoot].includes(
                    currFeature
                  ) &&
                  measure === CubeMeasureEnum.DetectionFactAvgLength
                ) {
                  return t('Length', { ns: 'common' });
                }

                return t(measure, { ns: 'common' });
              }}
              optionKeyGetter={(measure: CubeMeasureEnum) => measure}
              disableCriteria={(measure: CubeMeasureEnum) => {
                // Disable if the measure wasn't found in the customer summary or if on the ranch summary page and the measure is DetectionFactHasColorHSV since color doesn't make sense in the table
                return (
                  !servicesAndMeasuresWithFeatureFilter.availableMeasures.includes(
                    measure
                  ) ||
                  (ranchPageView === RanchPageViewEnum.ranchSummary &&
                    measure === CubeMeasureEnum.DetectionFactHasColorHSV)
                );
              }}
              disableDefaultEmptyValue={
                ranchPageView === RanchPageViewEnum.blockDetail ||
                (ranchPageView === RanchPageViewEnum.ranchSummary &&
                  ranchViewFeature === RanchViewFeaturesEnum.RanchMap)
              }
            />
          </DropdownContainer>
        )}
      </Box>
      <MapTableViewToggle />
    </Box>
  );
};

export default SelectHeader;

const DropdownContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  overflow: 'hidden',
  flex: 'initial',
});
